import { Fragment, useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, Paperclip, Plus, Users, type LucideIcon } from "lucide-react";
import type { PmColumn, PmMicroScenarioView, PmTag, PmTask } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Label } from "react-aria-components";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { AuthSession } from "../../auth/keycloak.js";
import { dropBefore } from "./pm-drop.js";
import { TagChip, TagDot, tagsOfTask } from "./pm-tags.js";
import { PmNewTaskDialog } from "./PmNewTaskDialog.js";
import { isStale, usePmTaskWrites } from "./usePmBoard.js";

const ALL = "all";
const NO_STEP = "none";
/** The filter's "Ohne Tag"; the tags themselves are their ids. */
const NO_TAG = "none";
/** The done column can grow long; it shows this many until the user asks for the rest (mockup "2b"). */
const DONE_PREVIEW = 5;

/** A spot in a column: before this task, or at the end (`null`). */
type DropSpot = { columnId: number; before: number | null };

/**
 * The Kanban (ADR-055 phase 6, mockup "2b"): the current phase's tasks in the realm's columns, filterable by step, by the
 * meeting flag and by tags: several tags mean "any of these", and the meeting flag and the step narrow that further. A card opens the task's details; it moves by drag & drop, where a placeholder of its size makes
 * room at the spot it would land on. Without dragging, the column is changed in the task's details; the card's own
 * "Verschieben nach …" menu was removed on the owner's call (2026-11-03), so re-sorting is drag & drop only. From `md` up the board fills the area below the filters: the columns scroll sideways inside it, so
 * the bar stays at the bottom of the window, and each column scrolls its own cards.
 *
 * A move sends the version the card was rendered with (D9). If someone changed the task meanwhile the server answers
 * 409; the board says so and reloads, so a second try works on fresh data.
 */
export function PmKanban({
  session,
  data,
  onOpenTask,
}: {
  session: AuthSession;
  data: PmMicroScenarioView;
  onOpenTask: (taskId: number) => void;
}) {
  const { t } = useTranslation();
  const writes = usePmTaskWrites(session);
  const [step, setStep] = useState<string>(ALL);
  const [onlyMeeting, setOnlyMeeting] = useState(false);
  const [tagFilter, setTagFilter] = useState<ReadonlySet<string>>(new Set());
  const [showAllDone, setShowAllDone] = useState(false);
  const [newTask, setNewTask] = useState(false);
  const [drag, setDrag] = useState<{ id: number; height: number; origin: DropSpot } | null>(null);
  const [drop, setDrop] = useState<DropSpot | null>(null);
  const liftTimer = useRef<number | undefined>(undefined);
  const dropped = useRef(false);
  const dragging = drag?.id ?? null;
  // Held until the move has settled and the board refetched, so the card never jumps back to where it came from.
  const endDrag = () => {
    window.clearTimeout(liftTimer.current);
    dropped.current = false;
    setDrag(null);
    setDrop(null);
  };
  const [announce, setAnnounce] = useState("");
  const [error, setError] = useState<string | null>(null);

  const doneCol = data.columns.find((c) => c.role === "done");
  const done = data.tasks.filter((x) => x.columnId === doneCol?.id).length;
  const flagged = data.tasks.filter((x) => x.flaggedForMeeting).length;
  const matchesTags = (x: PmTask) => tagFilter.size === 0 || (tagFilter.has(NO_TAG) && x.tagIds.length === 0) || x.tagIds.some((id) => tagFilter.has(String(id)));
  const visible = data.tasks.filter(
    (x) => (step === ALL || (step === NO_STEP ? x.stepId === null : x.stepId === Number(step))) && (!onlyMeeting || x.flaggedForMeeting) && matchesTags(x),
  );
  const toggleTag = (key: string) =>
    setTagFilter((cur) => {
      const next = new Set(cur);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const filterButton = (pressed: boolean) => ({ size: "sm", variant: pressed ? "secondary" : "ghost", "aria-pressed": pressed }) as const;
  const count = (n: number) => <span className="bg-muted text-muted-foreground rounded-full px-1.5 text-xs tabular-nums">{n}</span>;
  const stepLabel = (task: PmTask) => {
    const s = data.steps.find((x) => x.id === task.stepId);
    return s ? `${s.number} · ${s.title}` : t("pmBoard.kanban.wholeMs");
  };

  const move = (task: PmTask, column: PmColumn, beforeTaskId?: number | null) => {
    if (task.columnId === column.id && beforeTaskId === undefined) return Promise.resolve();
    setError(null);
    // The outcome hangs on THIS call's promise, not on `mutate` callbacks (diff panel): those run for the observer's
    // latest call only, so moving a second card before the first answered would swallow the first one's 409.
    return writes.move.mutateAsync({ id: task.id, version: task.version, columnId: column.id, beforeTaskId }).then(
      () => setAnnounce(t(task.columnId === column.id ? "pmBoard.kanban.reordered" : "pmBoard.kanban.moved", { title: task.title, column: column.title })),
      (e: unknown) => setError(isStale(e) ? t("pmBoard.kanban.stale") : t("pmBoard.kanban.moveFailed")),
    );
  };

  return (
    <div className="flex min-w-0 flex-col gap-4 md:min-h-0 md:flex-1">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="flex-1 text-2xl font-bold tracking-tight">
          {data.ms.code} – {data.ms.title}
        </h1>
        <span className="text-muted-foreground text-sm tabular-nums">{t("pmBoard.kanban.doneOf", { done, total: data.tasks.length })}</span>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <Select selectedKey={step} onSelectionChange={(k) => k !== null && setStep(String(k))} className="flex w-64 flex-col gap-1.5">
          <Label className="text-muted-foreground text-xs">{t("pmBoard.kanban.stepFilter")}</Label>
          <SelectTrigger />
          <SelectContent>
            <SelectItem id={ALL}>{t("pmBoard.kanban.allSteps")}</SelectItem>
            <SelectItem id={NO_STEP}>{t("pmBoard.kanban.noStep")}</SelectItem>
            {data.steps.map((s) => (
              <SelectItem key={s.id} id={String(s.id)}>{`${s.number} · ${s.title}`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div role="group" aria-label={t("pmBoard.kanban.taskFilter")} className="flex flex-wrap gap-1">
          <Button
            {...filterButton(!onlyMeeting && tagFilter.size === 0)}
            onPress={() => {
              setOnlyMeeting(false);
              setTagFilter(new Set());
            }}
          >
            {t("pmBoard.kanban.all")}
          </Button>
          <Button {...filterButton(onlyMeeting)} onPress={() => setOnlyMeeting((v) => !v)}>
            <Users aria-hidden="true" />
            {t("pmBoard.kanban.meeting")}
            {count(flagged)}
          </Button>
          {data.tags.map((g) => (
            <Button key={g.id} {...filterButton(tagFilter.has(String(g.id)))} onPress={() => toggleTag(String(g.id))}>
              <TagDot color={g.color} />
              {g.label}
              {count(data.tasks.filter((x) => x.tagIds.includes(g.id)).length)}
            </Button>
          ))}
          {data.tags.length > 0 ? (
            <Button {...filterButton(tagFilter.has(NO_TAG))} onPress={() => toggleTag(NO_TAG)}>
              {t("pmBoard.kanban.noTag")}
              {count(data.tasks.filter((x) => x.tagIds.length === 0).length)}
            </Button>
          ) : null}
        </div>
        <span className="flex-1" />
        {data.viewedPhaseKey !== null ? (
          <Button variant="outline" onPress={() => setNewTask(true)}>
            <Plus aria-hidden="true" />
            {t("pmBoard.newTask")}
          </Button>
        ) : null}
      </div>

      <p className="text-muted-foreground m-0 text-xs">{t("pmBoard.kanban.dragHint")}</p>
      {error ? (
        <p role="alert" className="text-destructive m-0 text-sm">
          {error}
        </p>
      ) : null}
      <p role="status" className="sr-only">
        {announce}
      </p>

      {/* The board scrolls sideways inside itself (D11: never the page). The scrollbar is coloured from the tokens: the
          app sets no `color-scheme`, so the browser's own bar stayed light-grey-on-white in the dark theme. `flex` on
          the region lets the columns stretch to its full height, so a whole column is a drop target. */}
      <div
        role="region"
        aria-label={t("pmBoard.kanban.boardLabel")}
        tabIndex={0}
        // The whole board takes the drag, not each column: over the gap between two columns the placeholder stays where it
        // is and a release lands there. Only off the board it returns to the card's own place, and a release does nothing.
        onDragOver={(e) => {
          if (dragging !== null) e.preventDefault();
        }}
        onDragLeave={(e) => {
          if (drag && !e.currentTarget.contains(e.relatedTarget as Node | null)) setDrop(drag.origin);
        }}
        onDrop={(e) => {
          e.preventDefault();
          const task = data.tasks.find((x) => x.id === dragging);
          const spot = drop ?? drag?.origin;
          const column = data.columns.find((c) => c.id === spot?.columnId);
          if (!task || !spot || !column) return;
          dropped.current = true;
          setDrop(spot);
          void move(task, column, spot.before).finally(endDrag);
        }}
        // The padding keeps a column's drag outline, which sits outside the column, inside this scroll container's clip.
        className="-mx-1 -mt-1 flex overflow-x-auto px-1 pt-1 pb-2 [scrollbar-color:var(--border-strong)_transparent] md:min-h-0 md:flex-1"
      >
        <div className="flex min-w-max gap-3">
          {data.columns.map((col) => {
            const tasks = visible.filter((x) => x.columnId === col.id);
            const isDone = col.role === "done";
            const shown = isDone && !showAllDone ? tasks.slice(0, DONE_PREVIEW) : tasks;
            // A position always comes from the whole column: "below this card" must not jump past what a filter or the
            // done preview hides. The dragged card has left its place, so it counts as a neighbour of nothing.
            const column = data.tasks.filter((x) => x.columnId === col.id && x.id !== dragging);
            const nextAfter = (task: PmTask | undefined) => (task ? (column[column.indexOf(task) + 1]?.id ?? null) : null);
            const stays = shown.filter((x) => x.id !== dragging);
            const afterShown = nextAfter(stays[stays.length - 1]);
            const headingId = `pm-col-${col.id}`;
            const dropPosition = (e: DragEvent<HTMLElement>) => {
              const before = dropBefore(
                Array.from(e.currentTarget.querySelectorAll<HTMLElement>("[data-pm-task]"), (el) => {
                  const r = el.getBoundingClientRect();
                  return { id: Number(el.dataset.pmTask), middle: r.top + r.height / 2 };
                }).filter((c) => c.id !== dragging),
                e.clientY,
                afterShown,
              );
              // Released where it already is, between the same visible neighbours: stay put, or a filter's hidden cards
              // would be jumped silently.
              const ownNext = shown[shown.findIndex((x) => x.id === dragging) + 1]?.id ?? null;
              const nextVisible = stays.some((x) => x.id === before) ? before : null;
              return drag?.origin.columnId === col.id && nextVisible === ownNext ? drag.origin.before : before;
            };
            // The card the placeholder stands in front of: the spot itself, or the next visible one after hidden cards.
            const spotIndex = drop?.columnId === col.id && drop.before !== null ? column.findIndex((x) => x.id === drop.before) : -1;
            const placeholderBefore = spotIndex < 0 ? null : (column.slice(spotIndex).find((x) => stays.includes(x))?.id ?? null);
            const dropHere = drop?.columnId === col.id;
            return (
              <section
                key={col.id}
                aria-labelledby={headingId}
                onDragOver={(e) => {
                  if (dragging === null) return;
                  e.preventDefault();
                  const before = dropPosition(e);
                  setDrop((d) => (d?.columnId === col.id && d.before === before ? d : { columnId: col.id, before }));
                }}
                className={cn(
                  "bg-muted/60 flex w-64 shrink-0 flex-col gap-2 rounded-xl p-2.5 md:min-h-0",
                  dragging !== null && "outline-border outline-2 outline-dashed",
                  dropHere && "outline-primary",
                )}
              >
                <div className="flex items-baseline gap-2 px-1">
                  <h2 id={headingId} className="min-w-0 text-sm font-semibold">
                    {col.title}
                  </h2>
                  {col.persistent ? <span className="text-muted-foreground text-xs">{t("pmBoard.kanban.persistent")}</span> : null}
                  <span className="bg-background ml-auto rounded-full px-2 text-xs tabular-nums">{tasks.length}</span>
                </div>
                {tasks.length === 0 && !dropHere ? <p className="text-muted-foreground m-0 px-1 py-2 text-xs">{t("pmBoard.kanban.empty")}</p> : null}
                {/* The padding keeps the cards' shadow and the focus ring inside this scroll container's clip. */}
                <ul className="m-0 flex list-none flex-col gap-2 p-0.5 md:min-h-0 md:flex-1 md:overflow-y-auto">
                  {shown.map((task) => (
                    <Fragment key={task.id}>
                      {dropHere && placeholderBefore === task.id ? <Placeholder height={drag?.height} /> : null}
                      {/* Kept mounted while dragged: unmounting the source would end the browser's drag. */}
                      <li data-pm-task={task.id} className={cn(drop !== null && task.id === dragging && "hidden")}>
                        <TaskCard
                          task={task}
                          stepLabel={stepLabel(task)}
                          tags={tagsOfTask(data.tags, task)}
                          onOpen={() => onOpenTask(task.id)}
                          onDragStart={(height) => {
                            // One drag at a time: the last drop still holds the drag state until its move has settled.
                            if (dropped.current) return false;
                            const origin = { columnId: col.id, before: nextAfter(task) };
                            setDrag({ id: task.id, height, origin });
                            // Not in this tick: changing the layout inside dragstart makes Chrome cancel the drag.
                            liftTimer.current = window.setTimeout(() => setDrop((d) => d ?? origin));
                            return true;
                          }}
                          onDragEnd={() => {
                            if (!dropped.current) endDrag();
                          }}
                          isMoving={writes.move.isPending && writes.move.variables?.id === task.id}
                        />
                      </li>
                    </Fragment>
                  ))}
                  {dropHere && placeholderBefore === null ? <Placeholder height={drag?.height} /> : null}
                </ul>
                {isDone && !showAllDone && tasks.length > DONE_PREVIEW ? (
                  <Button variant="ghost" size="sm" onPress={() => setShowAllDone(true)}>
                    {t("pmBoard.kanban.showMore", { count: tasks.length - DONE_PREVIEW })}
                  </Button>
                ) : null}
              </section>
            );
          })}
        </div>
      </div>

      {newTask ? <PmNewTaskDialog session={session} data={data} initialStepId={null} onClose={() => setNewTask(false)} /> : null}
    </div>
  );
}

/** Where the dragged card would land, as tall as the card, so the others make room for it. */
function Placeholder({ height }: { height: number | undefined }) {
  return <li aria-hidden="true" data-pm-placeholder="" className="border-primary bg-primary/10 shrink-0 rounded-lg border-2 border-dashed" style={{ height }} />;
}

function TaskCard({
  task,
  stepLabel,
  tags,
  onOpen,
  onDragStart,
  onDragEnd,
  isMoving,
}: {
  task: PmTask;
  stepLabel: string;
  tags: PmTag[];
  onOpen: () => void;
  onDragStart: (height: number) => boolean;
  onDragEnd: () => void;
  isMoving: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      draggable
      onDragStart={(e) => {
        if (!onDragStart(e.currentTarget.getBoundingClientRect().height)) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(task.id));
      }}
      onDragEnd={onDragEnd}
      className={cn("bg-background flex flex-col gap-2 rounded-lg p-3 shadow-sm", isMoving && "opacity-60")}
    >
      <span className="flex flex-col gap-0.5">
        <span title={stepLabel} className="text-muted-foreground truncate text-xs">
          {stepLabel}
        </span>
        <Button
          variant="ghost"
          onPress={onOpen}
          className="h-auto justify-start p-0 text-left text-sm leading-5 font-semibold whitespace-normal hover:bg-transparent hover:underline"
        >
          {task.title}
        </Button>
      </span>
      {/* One row: the tags on the left, counts and the meeting mark on the right. Only when there is something to show,
          or an empty row would only make the card taller. */}
      {tags.length > 0 || task.media.length > 0 || task.annotationCount > 0 || task.flaggedForMeeting ? (
        <span className="flex items-center gap-2">
          <span className="flex min-w-0 flex-wrap gap-1.5">
            {tags.map((g) => (
              <TagChip key={g.id} tag={g} />
            ))}
          </span>
          <span className="text-muted-foreground ml-auto flex shrink-0 items-center gap-3 text-xs">
            <CardCount icon={Paperclip} count={task.media.length} label={t("pmBoard.kanban.media", { count: task.media.length })} />
            <CardCount icon={MessageSquare} count={task.annotationCount} label={t("pmBoard.kanban.notes", { count: task.annotationCount })} />
            {task.flaggedForMeeting ? (
              <span title={t("pmBoard.kanban.meeting")}>
                <Users aria-hidden="true" className="size-4" />
                <span className="sr-only">{t("pmBoard.kanban.meeting")}</span>
              </span>
            ) : null}
          </span>
        </span>
      ) : null}
    </div>
  );
}

/** Icon + number on a card, only when there is something to count; the full text is for screen readers and the tooltip. */
function CardCount({ icon: Icon, count, label }: { icon: LucideIcon; count: number; label: string }) {
  if (count === 0) return null;
  return (
    <span title={label} className="inline-flex items-center gap-1 tabular-nums">
      <Icon aria-hidden="true" className="size-3" />
      <span aria-hidden="true">{count}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}
